/**
 * PayTierWithCrypto — one-click crypto checkout for platform tier subscriptions
 * (Starter / Trader / Pro / Elite).
 *
 * Reuses the same multi-chain, balance-detection, wagmi-driven flow built for
 * paid channels. Mainnet is intentionally blocked.
 *
 * Props:
 *   - tier:    'starter' | 'trader' | 'pro' | 'elite'
 *   - period:  'monthly' | 'annual'   (annual = ~50% discount)
 *   - onSuccess: callback after tier upgrade confirms
 *   - onClose: dismiss modal
 */

import { useEffect, useState } from 'react';
import { useAccount, useWalletClient, useChainId, useSwitchChain, useReadContracts } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { X, DollarSign, Loader2, AlertTriangle, Check, Copy, Zap, Crown } from 'lucide-react';
import { createTierUsdcCheckout, confirmTierUsdcCheckout } from '../services/api';

const PAY_CHAINS = [
  { key: 'base',     label: 'Base',     chainId: 8453,  usdc: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913', gasEstUsd: 0.02,  icon: '🔵' },
  { key: 'polygon',  label: 'Polygon',  chainId: 137,   usdc: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359', gasEstUsd: 0.005, icon: '🟣' },
  { key: 'arbitrum', label: 'Arbitrum', chainId: 42161, usdc: '0xaf88d065e77c8cc2239327c5edb3a432268e5831', gasEstUsd: 0.10,  icon: '🔷' },
];

const USDC_TRANSFER_ABI = [{
  type: 'function', name: 'transfer', stateMutability: 'nonpayable',
  inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }],
  outputs: [{ type: 'bool' }],
}];
const USDC_BALANCE_ABI = [{
  type: 'function', name: 'balanceOf', stateMutability: 'view',
  inputs: [{ name: 'account', type: 'address' }],
  outputs: [{ type: 'uint256' }],
}];

const TIER_INFO = {
  starter: { label: 'Starter', emoji: '🚀', monthly: 19.99,  annual: 120 },
  trader:  { label: 'Trader',  emoji: '📊', monthly: 65.99,  annual: 599.88 },
  pro:     { label: 'Pro',     emoji: '🧠', monthly: 99.99,  annual: 911.88 },
  elite:   { label: 'Elite',   emoji: '👑', monthly: 199.99, annual: 1799.88 },
};

export default function PayTierWithCrypto({ tier, period = 'annual', onSuccess, onClose }) {
  const tierInfo = TIER_INFO[tier];
  const [checkout, setCheckout] = useState(null);
  const [selectedChain, setSelectedChain] = useState('base');
  const [oneClickState, setOneClickState] = useState('idle');
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState(null);
  const [txHashManual, setTxHashManual] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const { address: walletAddress, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();

  // Read USDC balance on all 3 chains
  const balanceQueries = walletAddress ? PAY_CHAINS.map(c => ({
    address: c.usdc, abi: USDC_BALANCE_ABI, functionName: 'balanceOf', args: [walletAddress], chainId: c.chainId,
  })) : [];
  const { data: balanceData } = useReadContracts({
    contracts: balanceQueries,
    query: { enabled: !!walletAddress, refetchInterval: 30000 },
  });
  const usdcBalances = PAY_CHAINS.map((c, i) => ({
    ...c,
    balance: balanceData?.[i]?.result ? Number(balanceData[i].result) / 1e6 : 0,
  }));

  // Create the checkout record on backend when modal opens
  useEffect(() => {
    if (checkout) return;
    createTierUsdcCheckout({ tier, period, chain: selectedChain, subscriberWallet: walletAddress })
      .then(setCheckout)
      .catch(e => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, period]);

  // Auto-pick cheapest chain with sufficient balance
  useEffect(() => {
    if (!checkout || !walletAddress || !balanceData) return;
    const required = checkout.finalPrice;
    for (const k of ['polygon', 'base', 'arbitrum']) {
      const c = usdcBalances.find(b => b.key === k);
      if (c && c.balance >= required) { setSelectedChain(k); return; }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout?.finalPrice, walletAddress, balanceData]);

  async function payWithWallet() {
    if (!walletClient || !walletAddress || !checkout) return;
    const target = PAY_CHAINS.find(c => c.key === selectedChain) || PAY_CHAINS[0];
    setError(null);
    setOneClickState('switching');
    try {
      if (chainId !== target.chainId) await switchChainAsync({ chainId: target.chainId });

      setOneClickState('signing');
      const amountWei = BigInt(Math.floor(checkout.finalPrice * 1e6));
      const hash = await walletClient.writeContract({
        address: target.usdc,
        abi: USDC_TRANSFER_ABI,
        functionName: 'transfer',
        args: [checkout.payToWallet, amountWei],
        chainId: target.chainId,
      });

      try { localStorage.setItem(`pendingTierPay:${checkout.checkoutId}`, JSON.stringify({ hash, chain: target.key, ts: Date.now() })); } catch {}

      setOneClickState('confirming');
      // Update the checkout with the actual chain user paid on (in case they changed)
      let attempts = 0;
      while (attempts < 10) {
        const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/usdc-checkout/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
          body: JSON.stringify({ checkoutId: checkout.checkoutId, txHash: hash }),
        });
        const data = await res.json();
        const real = data.data || data;
        if (res.ok && !real.error) {
          setActivated(true);
          try { localStorage.removeItem(`pendingTierPay:${checkout.checkoutId}`); } catch {}
          if (onSuccess) onSuccess(real);
          setOneClickState('idle');
          return;
        }
        if (!(real.error || '').includes('not found on-chain')) {
          throw new Error(real.error || `HTTP ${res.status}`);
        }
        attempts++;
        await new Promise(r => setTimeout(r, 3000));
      }
      throw new Error('Tx not found on-chain after 30s — try the manual confirm with this hash');
    } catch (e) {
      const msg = e?.shortMessage || e?.message || 'Payment failed';
      setError(
        msg.includes('User rejected') || msg.includes('User denied') ? 'Payment cancelled in wallet' :
        msg.includes('insufficient funds') || msg.includes('exceeds balance') ? `Insufficient USDC on ${target.label}` :
        msg
      );
      setOneClickState('error');
    }
  }

  async function confirmManual() {
    if (!/^0x[a-fA-F0-9]{64}$/.test(txHashManual)) {
      setError('Invalid tx hash');
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const result = await confirmTierUsdcCheckout({ checkoutId: checkout.checkoutId, txHash: txHashManual });
      setActivated(true);
      if (onSuccess) onSuccess(result);
    } catch (e) {
      setError(e.message);
    }
    setConfirming(false);
  }

  if (activated) {
    return (
      <ModalShell onClose={onClose} title={`Welcome to ${tierInfo.label}`}>
        <div className="py-6 text-center">
          <div className="w-16 h-16 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-emerald-400" />
          </div>
          <p className="text-lg font-bold text-white mb-1">{tierInfo.emoji} You're {tierInfo.label} now</p>
          <p className="text-xs text-gray-400 mb-6">All {tierInfo.label} features unlocked. {period === 'annual' ? 'Valid for 1 year.' : 'Renews monthly.'}</p>
          <button onClick={onClose} className="px-6 py-3 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm">Got it</button>
        </div>
      </ModalShell>
    );
  }

  if (!checkout) {
    return (
      <ModalShell onClose={onClose} title={`Pay for ${tierInfo.label}`}>
        <div className="py-12 flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          <p className="text-xs text-gray-500">Creating checkout…</p>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title={`${tierInfo.emoji} Pay for ${tierInfo.label} with Crypto`}>
      <div className="space-y-4">
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">{checkout.period === 'annual' ? 'Annual' : 'Monthly'} subscription</p>
              <p className="text-2xl font-bold text-white mt-0.5">${checkout.finalPrice} <span className="text-sm text-gray-500">USDC</span></p>
              {checkout.discount > 0 && (
                <p className="text-[11px] text-amber-400 mt-0.5">Founders {checkout.discount}% off · was ${checkout.originalPrice}</p>
              )}
            </div>
            <span className="text-3xl">{tierInfo.emoji}</span>
          </div>
        </div>

        {/* Mainnet warning */}
        {isConnected && chainId === 1 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-red-200">
              <p className="font-bold text-red-300">Ethereum mainnet not supported</p>
              <p className="mt-0.5">Mainnet gas ($5-15) would eat your discount. Switch to Base, Polygon, or Arbitrum.</p>
            </div>
          </div>
        )}

        {/* Connected wallet flow */}
        {isConnected && walletAddress ? (
          <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <p className="text-xs font-bold text-emerald-300">Pay with your wallet</p>
              </div>
              <p className="text-[10px] text-gray-500 font-mono">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</p>
            </div>

            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Pay from which chain?</p>
              <div className="space-y-1.5">
                {usdcBalances.map(c => {
                  const enough = c.balance >= checkout.finalPrice;
                  return (
                    <button key={c.key} onClick={() => enough && setSelectedChain(c.key)} disabled={!enough}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${
                        selectedChain === c.key && enough ? 'bg-emerald-500/20 border-emerald-500/50' :
                        enough ? 'bg-dark-800/50 border-dark-600 hover:border-dark-500' :
                        'bg-dark-800/30 border-dark-700 opacity-50 cursor-not-allowed'
                      }`}>
                      <span className="text-base">{c.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white">{c.label}</p>
                        <p className="text-[10px] text-gray-500">
                          Gas: ~${c.gasEstUsd.toFixed(3)} · You have: <span className={enough ? 'text-emerald-400' : 'text-amber-400'}>${c.balance.toFixed(2)} USDC</span>
                        </p>
                      </div>
                      {selectedChain === c.key && enough && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {!usdcBalances.some(c => c.balance >= checkout.finalPrice) && (
                <p className="text-[11px] text-amber-400 mt-2">
                  ⚠ You don't have ${checkout.finalPrice} USDC on any supported chain. Buy on Coinbase → withdraw to one of the chains above.
                </p>
              )}
            </div>

            <button onClick={payWithWallet}
              disabled={oneClickState === 'switching' || oneClickState === 'signing' || oneClickState === 'confirming' || (usdcBalances.find(c => c.key === selectedChain)?.balance ?? 0) < checkout.finalPrice}
              className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-dark-900 font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
              {oneClickState === 'switching' ? <><Loader2 className="w-4 h-4 animate-spin" /> Switching to {PAY_CHAINS.find(c => c.key === selectedChain)?.label}…</> :
               oneClickState === 'signing'   ? <><Loader2 className="w-4 h-4 animate-spin" /> Confirm in wallet…</> :
               oneClickState === 'confirming'? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying on-chain…</> :
               <><DollarSign className="w-4 h-4" /> Pay ${checkout.finalPrice} USDC on {PAY_CHAINS.find(c => c.key === selectedChain)?.label}</>}
            </button>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-emerald-300 text-center">⚡ Connect wallet for 1-click pay</p>
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <button onClick={openConnectModal}
                  className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-dark-900 font-bold text-sm flex items-center justify-center gap-2 transition-all">
                  <Zap className="w-4 h-4" /> Connect Wallet
                </button>
              )}
            </ConnectButton.Custom>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 text-xs text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Manual fallback */}
        <button onClick={() => setShowManual(!showManual)}
          className="w-full text-[11px] text-gray-500 hover:text-white py-1 flex items-center justify-center gap-1">
          <span className={`transition-transform inline-block ${showManual ? 'rotate-90' : ''}`}>▶</span>
          Or paste tx hash manually (from Coinbase or another wallet)
        </button>

        {showManual && (
          <div className="bg-dark-700/30 border border-dark-700/50 rounded-xl p-3 space-y-2">
            <p className="text-[11px] text-gray-500">
              Send ${checkout.finalPrice} USDC on Base to <code className="text-amber-400">{checkout.payToWallet.slice(0, 8)}...{checkout.payToWallet.slice(-6)}</code>, then paste your tx hash:
            </p>
            <input type="text" value={txHashManual} onChange={e => setTxHashManual(e.target.value.trim())} maxLength={66}
              placeholder="0x..."
              className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white text-xs font-mono placeholder-gray-600 focus:border-amber-400 focus:outline-none" />
            <button onClick={confirmManual} disabled={confirming || !txHashManual}
              className="w-full py-2 rounded-lg bg-amber-500/80 hover:bg-amber-500 text-dark-900 font-bold text-xs disabled:opacity-40">
              {confirming ? 'Verifying…' : 'Verify & activate'}
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
